import { Typography } from '@/components/ui/typography'
import { useTopGainers } from '@/hooks/use-top-gainers'
import Link from 'next/link'
import numeral from 'numeral'
import { Avatar, AvatarImage } from '@/components/ui/avatar'
import { useRecommendedPortfolios } from '@/hooks/use-recommended-portfolios'
import { Portfolio } from '@/types/portfolio'
import { useMemo } from 'react'
import { Tokens } from '@/components/portfolio/tokens'
import { TopGainers } from '@/components/portfolio/top-gainers'

const RecommendedPortfolio = ({ portfolio }: { portfolio: Portfolio }) => {
  const protocolCount = useMemo(() => {
    let count = 0
    if (Number(portfolio.hyperswapV3.totalValue) > 0) {
      count += 1
    }
    if (Number(portfolio.hyperlend.totalValue) > 0) {
      count += 1
    }
    if (Number(portfolio.felix.totalValue) > 0) {
      count += 1
    }

    return count
  }, [portfolio])

  return (
    <Link
      className="flex items-start gap-4 border rounded-xl px-4 py-4 hover:border-foreground/20 transition duration-300 bg-background opacity-100"
      href={`/portfolio/${portfolio.address}`}
    >
      <Avatar className={'md:size-14 size-12 md:rounded-xl rounded-xl'}>
        <AvatarImage src={`https://cdn.stamp.fyi/avatar/${portfolio.address}`} alt={'avatar'} className="w-full" />
      </Avatar>
      <div>
        <Typography className={'font-semibold'}>{portfolio.label}</Typography>
        <Typography variant={'h3'} className={'font-ibm-sans'}>
          ${numeral(portfolio.totalValue).format('0,0')}
        </Typography>
        <Typography variant={'tip'} className="flex mt-1">
          {portfolio.assets.assets.length} Assets • {protocolCount} Protocols • {portfolio.nft.portfolio.totalTokens}{' '}
          NFTs
        </Typography>
      </div>
    </Link>
  )
}

const IndexPage = () => {
  const { recommendedPortfolios, isLoading: isPortfolioLoading } = useRecommendedPortfolios()

  return (
    <div className={'max-w-320 mx-auto px-4 pb-20'}>
      <div className="grid md:grid-cols-2 md:gap-6 gap-12 mb-24">
        <TopGainers />
        <TopGainers reversed />
      </div>
      <Typography variant={'h4'} className={'mb-6 font-averia-libre'}>
        Portfolios
      </Typography>
      <div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-4 mb-24 relative z-10">
        {isPortfolioLoading && (
          <div className="border rounded-xl p-4 flex gap-4">
            <div className="bg-foreground/5 md:size-20 size-12 rounded-2xl" />
            <div>
              <div className="bg-foreground/5 w-16 h-5 rounded" />
              <div className="bg-foreground/5 w-32 h-7 rounded my-2" />
              <div className="bg-foreground/5 w-12 h-3 rounded" />
            </div>
          </div>
        )}
        {recommendedPortfolios?.map((p) => (
          <RecommendedPortfolio portfolio={p} />
        ))}
      </div>
      <Typography variant={'h3'} className={'mb-6 hidden'}>
        HyperNews
      </Typography>
      <div className="flex-col gap-4 mb-24 hidden">
        <Link
          href={'https://x.com/USDT0_to/status/1920841189000884565'}
          target={'_blank'}
          className={'flex items-start gap-4'}
        >
          <Typography>USDT0 is live on @HyperliquidX Spot and HyperEVM.</Typography>
        </Link>
      </div>
      <Typography variant={'h3'} className={'mb-6 hidden'}>
        DAPP
      </Typography>
      <div className="md:grid-cols-4 grid-cols-1 space-x-4 space-y-8 mb-24 hidden">
        <Link className="flex items-start gap-4" href={'https://app.hyperswap.exchange/#/swap'}>
          <img
            src={'https://dropjet.co/wp-content/uploads/2024/10/HyperSwap-Logo-150x150.jpg'}
            width={68}
            height={68}
            alt={'hyperevm'}
            className={'rounded-lg aspect-square w-[68px]'}
          />
          <div>
            <div className="flex justify-between">
              <Typography variant={'large'}>HyperSwap</Typography>
            </div>
            <Typography variant={'tip'} className={'text-foreground/60'}>
              Explore token swaps, liquidity management and easy token launches
            </Typography>
          </div>
        </Link>
        <Link className="flex items-start gap-4" href={'https://kittenswap.finance/'}>
          <img
            src={'https://kittenswap.finance/logo-mark.svg'}
            width={68}
            height={68}
            alt={'hyperevm'}
            className={'rounded-lg aspect-square bg-black'}
          />
          <div>
            <div className="flex justify-between">
              <Typography variant={'large'}>KittenSwap</Typography>
            </div>
            <Typography variant={'tip'} className={'text-foreground/60'}>
              The community owned DEX for seamless trading on HyperEVM!
            </Typography>
          </div>
        </Link>
        <Link className="flex items-start gap-4" href={'https://kittenswap.finance/'}>
          <img
            src={'https://kittenswap.finance/logo-mark.svg'}
            width={68}
            height={68}
            alt={'hyperevm'}
            className={'rounded-lg aspect-square bg-black'}
          />
          <div>
            <div className="flex justify-between">
              <Typography variant={'large'}>KittenSwap</Typography>
            </div>
            <Typography variant={'tip'} className={'text-foreground/60'}>
              The community owned DEX for seamless trading on HyperEVM!
            </Typography>
          </div>
        </Link>
        <Link className="flex items-start gap-4" href={'https://kittenswap.finance/'}>
          <img
            src={'https://kittenswap.finance/logo-mark.svg'}
            width={68}
            height={68}
            alt={'hyperevm'}
            className={'rounded-lg aspect-square bg-black'}
          />
          <div>
            <div className="flex justify-between">
              <Typography variant={'large'}>KittenSwap</Typography>
            </div>
            <Typography variant={'tip'} className={'text-foreground/60'}>
              The community owned DEX for seamless trading on HyperEVM!
            </Typography>
          </div>
        </Link>
        <Link className="flex items-start gap-4" href={'https://kittenswap.finance/'}>
          <img
            src={'https://kittenswap.finance/logo-mark.svg'}
            width={68}
            height={68}
            alt={'hyperevm'}
            className={'rounded-lg aspect-square bg-black'}
          />
          <div>
            <div className="flex justify-between">
              <Typography variant={'large'}>KittenSwap</Typography>
            </div>
            <Typography variant={'tip'} className={'text-foreground/60'}>
              The community owned DEX for seamless trading on HyperEVM!
            </Typography>
          </div>
        </Link>
      </div>
      <Typography variant={'h4'} className={'mb-6 font-averia-libre'}>
        Tokens
      </Typography>
      <Tokens />
    </div>
  )
}

IndexPage.chains = []

export default IndexPage
